import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import httpService from '../../../services/http.service';
import AppBreadcrumb from '../../Shared/AppBreadcrumb';
import AppAlert from '../../Shared/AppAlert';
import KpiCard from '../../Shared/KpiCard';
import { ShimmerRows } from '../../Shared/Shimmer';
import { extractResponseValidationErrors } from '../../../utils/index';

const SOURCES = [
    { key: 'coins', label: 'Pièces', icon: 'fa-database', step: 1, integer: true },
    { key: 'likes', label: 'Likes', icon: 'fa-heart', step: 1, integer: true },
    { key: 'views', label: 'Vues', icon: 'fa-eye', step: 1, integer: true },
    { key: 'special_content', label: 'Contenus spéciaux', icon: 'fa-star', step: 0.01, integer: false },
];

const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

/**
 * Retrait manuel : on recherche un utilisateur, on choisit ce qui est retiré (pièces, likes, vues, contenus
 * spéciaux) et on voit la somme équivalente en USD à lui régler hors plateforme. La confirmation débite les
 * soldes et enregistre le retrait (référence obligatoire).
 */
const ManualWithdrawal = () => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    const usd = (value) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);
    const int = (value) => new Intl.NumberFormat(locale).format(Number(value) || 0);

    const [username, setUsername] = useState('');
    const [lookup, setLookup] = useState({ loading: false, error: '', data: null });
    const [amounts, setAmounts] = useState({ coins: '', likes: '', views: '', special_content: '' });
    const [reference, setReference] = useState('');
    const [note, setNote] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const balances = lookup.data?.balances;
    const user = lookup.data?.user;

    const search = async (event) => {
        if (event) event.preventDefault();
        const value = username.trim();
        if (!value) return;
        setLookup({ loading: true, error: '', data: null });
        setAmounts({ coins: '', likes: '', views: '', special_content: '' });
        setResult(null);
        setError('');
        try {
            const { data } = await httpService.get('/payments/manual-withdrawal/balances', { params: { username: value } });
            setLookup({ loading: false, error: '', data: data.data });
        } catch (e) {
            setLookup({ loading: false, error: e?.response?.status === 404 ? t('Utilisateur introuvable.') : t('Impossible de charger les soldes.'), data: null });
        }
    };

    const lines = useMemo(() => {
        if (!balances) return [];
        return SOURCES.map((source) => {
            const info = balances[source.key];
            const rate = source.key === 'special_content' ? 1 : balances.rates[{ coins: 'coin', likes: 'like', views: 'view' }[source.key]];
            const value = Number(amounts[source.key]) || 0;
            return {
                ...source,
                available: info.available,
                rate,
                value,
                usd: round2(value * rate),
                invalid: value < 0 || value > info.available + 0.00001 || (source.integer && !Number.isInteger(value)),
            };
        });
    }, [balances, amounts]);

    const totalUsd = round2(lines.reduce((sum, line) => sum + line.usd, 0));
    const hasInvalid = lines.some((line) => line.invalid);
    const canSubmit = !!balances && totalUsd > 0 && !hasInvalid && reference.trim().length > 0;

    const fillAll = () => setAmounts(SOURCES.reduce((acc, source) => ({ ...acc, [source.key]: balances[source.key].available || '' }), {}));

    const openConfirm = () => { setAccepted(false); setError(''); setConfirming(true); };

    const submit = async () => {
        setSubmitting(true);
        setError('');
        try {
            const { data } = await httpService.post('/payments/manual-withdrawal', {
                user_id: user.user_id,
                coins: lines[0].value,
                likes: lines[1].value,
                views: lines[2].value,
                special_content: lines[3].value,
                reference: reference.trim(),
                note: note.trim() || null,
                expected_usd: totalUsd,
            });
            setResult(data.data);
            setConfirming(false);
            setLookup((state) => ({ ...state, data: { ...state.data, balances: data.data.balances } }));
            setAmounts({ coins: '', likes: '', views: '', special_content: '' });
            setReference('');
            setNote('');
        } catch (e) {
            const response = e?.response;
            setError(response?.status === 422 ? extractResponseValidationErrors(response.data)[0] : (response?.data?.message || t('Erreur de traitement, veuillez contacter les administrateurs')));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t('Retrait manuel')} />
            <p className="text-muted">
                {t("Débitez les pièces, likes, vues et gains de contenus spéciaux d'un utilisateur en échange de la somme équivalente en USD, réglée hors plateforme (virement, espèces, Mobile Money…).")}
            </p>

            <div className="card">
                <div className="body">
                    <form className="form-inline manual-search" onSubmit={search}>
                        <label className="mr-2 font-weight-bold" htmlFor="mw-username">{t("Nom d'utilisateur")}</label>
                        <div className="input-group mr-2">
                            <div className="input-group-prepend"><span className="input-group-text">@</span></div>
                            <input id="mw-username" className="form-control" value={username} placeholder="username" autoComplete="off"
                                   onChange={(e) => setUsername(e.target.value)} />
                        </div>
                        <button type="submit" className="btn btn-primary font-weight-bold" disabled={lookup.loading || !username.trim()}>
                            <i className="fa fa-search" aria-hidden="true" /> {t('Rechercher')}
                        </button>
                    </form>
                    {lookup.error && <div className="text-danger mt-3">{lookup.error}</div>}
                </div>
            </div>

            {lookup.loading && <div className="card"><div className="body"><ShimmerRows rows={5} /></div></div>}

            {result && (
                <div className="alert alert-success manual-receipt" role="status">
                    <i className="fa fa-check-circle mr-2" aria-hidden="true" />
                    <strong>{t('Retrait enregistré')}</strong> : {usd(result.usd)} — {t('référence')} <code>{result.request_id}</code>.
                    {' '}<Link to="/payments">{t('Voir les retraits')}</Link>
                    <div className="small mt-1">
                        {result.notified
                            ? <><i className="fa fa-bell mr-1" aria-hidden="true" />{t("L'utilisateur a été notifié (application et push).")}</>
                            : <><i className="fa fa-bell-slash mr-1" aria-hidden="true" />{t("Le retrait est enregistré, mais la notification n'a pas pu être envoyée.")}</>}
                    </div>
                </div>
            )}

            {balances && (
                <>
                    <div className="row clearfix equal-height-row">
                        <div className="col-lg-4 col-12">
                            <KpiCard icon="fa-user" tone="blue" label={t('Utilisateur')}
                                value={`${user.user_surname || ''} ${user.user_name || ''}`.trim() || `@${user.user_username}`}
                                hint={`@${user.user_username}${user.user_email ? ` · ${user.user_email}` : ''}`} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <KpiCard icon="fa-money" tone="green" label={t('Total retirable')} value={usd(balances.total_usd)} hint={t('Équivalent USD de tous les soldes')} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <KpiCard icon="fa-exchange" tone="orange" label={t('Retrait en cours de saisie')} value={usd(totalUsd)}
                                hint={totalUsd > 0 ? t("Somme à régler à l'utilisateur") : t('Saisissez des quantités ci-dessous')} />
                        </div>
                    </div>

                    <div className="card">
                        <div className="header d-flex justify-content-between align-items-center">
                            <h2>{t('Ce qui est retiré')}</h2>
                            <button type="button" className="btn btn-outline-primary btn-sm font-weight-bold" onClick={fillAll}>
                                <i className="fa fa-level-down" aria-hidden="true" /> {t('Tout retirer')}
                            </button>
                        </div>
                        <div className="table-responsive">
                            <table className="table mb-0 manual-table">
                                <thead>
                                    <tr>
                                        <th>{t('Source')}</th>
                                        <th className="text-right">{t('Disponible')}</th>
                                        <th className="text-right">{t('Valeur unitaire')}</th>
                                        <th style={{ width: 200 }}>{t('À retirer')}</th>
                                        <th className="text-right">{t('Équivalent USD')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((line) => (
                                        <tr key={line.key}>
                                            <td><i className={`fa ${line.icon} mr-2 stats-card-icon`} aria-hidden="true" /><strong>{t(line.label)}</strong></td>
                                            <td className="text-right">{line.integer ? int(line.available) : usd(line.available)}</td>
                                            <td className="text-right text-muted">{line.key === 'special_content' ? '1 USD' : `${line.rate} USD`}</td>
                                            <td>
                                                <div className="input-group input-group-sm">
                                                    <input type="number" min="0" max={line.available} step={line.step} inputMode="decimal"
                                                           className={`form-control ${line.invalid ? 'is-invalid' : ''}`} value={amounts[line.key]}
                                                           disabled={line.available <= 0} aria-label={t(line.label)}
                                                           onChange={(e) => setAmounts({ ...amounts, [line.key]: e.target.value })} />
                                                    <div className="input-group-append">
                                                        <button type="button" className="btn btn-outline-secondary" disabled={line.available <= 0}
                                                                onClick={() => setAmounts({ ...amounts, [line.key]: line.available })}>{t('Max')}</button>
                                                    </div>
                                                </div>
                                                {line.invalid && <small className="text-danger">{t('Dépasse le solde disponible')}</small>}
                                            </td>
                                            <td className="text-right font-weight-bold">{usd(line.usd)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={4} className="text-right font-weight-bold">{t('Somme équivalente à régler')}</td>
                                        <td className="text-right manual-total">{usd(totalUsd)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    <div className="card">
                        <div className="body">
                            <div className="row">
                                <div className="col-md-5">
                                    <div className="form-group">
                                        <label className="control-label" htmlFor="mw-reference">{t('Référence du règlement')} <span className="text-danger">*</span></label>
                                        <input id="mw-reference" className="form-control" maxLength={120} value={reference}
                                               placeholder={t('N° de virement, de reçu, de transaction Mobile Money…')} onChange={(e) => setReference(e.target.value)} />
                                    </div>
                                </div>
                                <div className="col-md-7">
                                    <div className="form-group">
                                        <label className="control-label" htmlFor="mw-note">{t('Note (facultatif)')}</label>
                                        <input id="mw-note" className="form-control" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="btn btn-success font-weight-bold" disabled={!canSubmit} onClick={openConfirm}>
                                <i className="fa fa-check" aria-hidden="true" /> {t('Effectuer le retrait')}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className={`modal fade ${confirming ? 'd-block show' : ''}`} style={{ overflowY: 'auto' }} onClick={() => null}>
                <div className="modal-dialog" role="document">
                    {confirming && (
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('Confirmer le retrait')}</h5>
                                <button type="button" className="close" aria-label="Close" onClick={() => setConfirming(false)}><span aria-hidden="true">×</span></button>
                            </div>
                            <div className="modal-body">
                                {error && <AppAlert onClose={() => setError('')} type="danger">{error}</AppAlert>}
                                <p>{t('Retrait pour')} <strong>@{user.user_username}</strong> :</p>
                                <ul className="manual-summary">
                                    {lines.filter((line) => line.value > 0).map((line) => (
                                        <li key={line.key}><span>{t(line.label)}</span><span>{line.integer ? int(line.value) : usd(line.value)} → {usd(line.usd)}</span></li>
                                    ))}
                                    <li className="manual-summary__total"><span>{t('Total')}</span><span>{usd(totalUsd)}</span></li>
                                </ul>
                                <p className="text-muted small mb-3">{t('Référence')} : {reference}</p>
                                <div className="fancy-checkbox">
                                    <label>
                                        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                                        <span>{t("Je confirme que {{amount}} a été ou sera réglé à l'utilisateur. Les soldes seront débités et cette action est définitive.", { amount: usd(totalUsd) })}</span>
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-round btn-danger font-weight-bold" disabled={submitting} onClick={() => setConfirming(false)}>{t('Annuler')}</button>
                                <button type="button" className="btn btn-round btn-success font-weight-bold" disabled={!accepted || submitting} onClick={submit}>
                                    {submitting ? t('Enregistrement...') : t('Confirmer le retrait')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManualWithdrawal;
